import React from 'react';
import { Box } from '@osmosis/tailwind-ui/components/Box';
import { Flex } from '@osmosis/tailwind-ui/components/Box';
import { H2 } from '@osmosis/tailwind-ui/components/Text';
import { borderClasses200 } from '@osmosis/tailwind-ui/styles/border';
import Image from 'next/image';
import { LoggedOutProductLibraryContainsItem } from '../../../types/library';
import { toSentenceCase } from '../../../lib/shared/string/capitalize';
import { formattedDataId } from '../../../lib/library/format';
import L3Item from './L3Item';
import ScrollToElement from '@osmosis/nextjs-components/ScrollToElement';
import { replaceS3ToCloudfront } from '../../../lib/util/url';
import AnswerBodyCTA from '../../answers/AnswerBodyCTA';

type Props = {
  l1: LoggedOutProductLibraryContainsItem;
  l2: LoggedOutProductLibraryContainsItem;
  targetId?: string;
  showCta?: boolean;
};

/**
 * L2Item.
 */
const L2Item = ({ l1, l2, targetId, showCta = false }: Props) => {
  if (!l2.contains) return null;
  const elementId = encodeURI(`${l1.id}-${l2.id}-L2`);
  const isTargetElement = targetId === elementId;
  const l2IconUrl = replaceS3ToCloudfront(l2.icon);
  return (
    <Box
      cn={`m-2 sm:mx-0 py-2 overflow-auto border-1 ${borderClasses200} rounded-lg`}
      key={l2.id}
      data-id={l2.id}
      id={formattedDataId(l1.path, l2.name)}
    >
      <Flex cn="p-8 items-center gap-2 border-b-1">
        {l2IconUrl ? (
          <Image
            data-testid="library-topic-box-icon"
            width={25}
            height={25}
            alt="topic icon"
            src={l2IconUrl}
          />
        ) : null}

        {isTargetElement && <ScrollToElement elementId={elementId} />}
        <H2 data-testid="topic-name" id={elementId}>
          {toSentenceCase(l2.displayName || l2.name)}
        </H2>
      </Flex>
      <Box cn="p-2">
        {l2.contains?.map((l3, index) => {
          return (
            <React.Fragment key={'l3' + l3.id}>
              <L3Item l1={l1} l2={l2} l3={l3} {...(targetId && { targetId })} />
              {/* Show the trial CTA once, after the first L3 of the first L2 of the first L1. */}
              {showCta && index === 0 && (
                <AnswerBodyCTA sourceCTA="library-body-cta" />
              )}
            </React.Fragment>
          );
        })}
      </Box>
    </Box>
  );
};

export default L2Item;
